import { Device } from '@capacitor/device';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import type { Locale } from 'antd/es/locale';

import('dayjs/locale/fr');
import('dayjs/locale/en');
import('dayjs/locale/es');
import('dayjs/locale/pt');
import('dayjs/locale/it');
import('dayjs/locale/de');

const localeModules = import.meta.glob('../locales/**/*.json');

export const loadLocaleResources = async (
  lng: string,
  modules: Record<string, () => Promise<unknown>> = localeModules,
): Promise<Record<string, Record<string, unknown>>> => {
  const prefix = `../locales/${lng}/`;
  const entries = Object.entries(modules).filter(([path]) =>
    path.startsWith(prefix),
  );
  const settled = await Promise.allSettled(
    entries.map(async ([path, load]) => {
      const ns = path.slice(prefix.length).replace(/\.json$/, '');
      const mod = (await load()) as { default: Record<string, unknown> };
      return [ns, mod.default] as const;
    }),
  );

  const failedNamespaces: string[] = [];
  const namespaces = settled.flatMap((result, index) => {
    if (result.status === 'fulfilled') {
      return [result.value];
    }
    const [path] = entries[index];
    failedNamespaces.push(path.slice(prefix.length).replace(/\.json$/, ''));
    return [];
  });

  if (failedNamespaces.length > 0) {
    console.error(
      `[i18n] failed to load locale chunk(s) for "${lng}": ${failedNamespaces.join(', ')} — those keys will render raw`,
    );
  }

  return { [lng]: Object.fromEntries(namespaces) };
};

const syncDocumentLanguage = (language?: string) => {
  if (language) document.documentElement.lang = language;
};

export const initTranslation = async (
  // Test seam only: `loadResourcesForLanguage` defaults to the real `loadLocaleResources`.
  loadResourcesForLanguage: (
    lng: string,
  ) => Promise<Record<string, Record<string, unknown>>> = loadLocaleResources,
  { isDocumentLangSynced = true }: { isDocumentLangSynced?: boolean } = {},
) => {
  const { value: lng } = await Device.getLanguageCode();

  dayjs.locale(lng);
  dayjs.extend(duration);
  dayjs.extend(relativeTime);
  dayjs.extend(localizedFormat);
  dayjs.extend(isSameOrBefore);

  // Only the detected + fallback languages are awaited here, loaded in parallel (one boot
  // round-trip, not two serial ones — both fit well inside runLocalInit's 15s budget either way,
  // but there's no reason to pay for two when one suffices).
  const loadLanguage = async (
    candidate: string,
  ): Promise<Record<string, Record<string, unknown>>> => {
    try {
      return await loadResourcesForLanguage(candidate);
    } catch (error) {
      console.error(`[i18n] loadLocaleResources rejected for "${candidate}"`, error);
      return { [candidate]: {} };
    }
  };

  const languagesToLoad = Array.from(new Set([lng, 'en']));
  const loadedResults = await Promise.all(languagesToLoad.map(loadLanguage));
  const loadedLanguages: Record<string, Record<string, unknown>> = Object.assign(
    {},
    ...loadedResults,
  );
  const resources: Record<string, { translation: Record<string, unknown> }> = {};
  Object.entries(loadedLanguages).forEach(([locale, namespaces]) => {
    resources[locale] = { translation: namespaces };
  });

  await i18n
    .use(initReactI18next)
    .init({
      resources,
      supportedLngs: ['fr', 'en', 'es', 'pt', 'it', 'de'],
      nonExplicitSupportedLngs: true,
      fallbackLng: 'en',
      lng,

      interpolation: {
        escapeValue: false,
      },
    });

  if (isDocumentLangSynced) {
    syncDocumentLanguage(i18n.language);
    i18n.off('languageChanged', syncDocumentLanguage);
    i18n.on('languageChanged', syncDocumentLanguage);
  }

  i18n.services.formatter?.add('lowercase', (value, lng) =>
    value.toLocaleLowerCase(lng ?? undefined),
  );
};

export const getAntdLocale = async (): Promise<Locale> => {
  const language = i18n.language || 'en';

  try {
    switch (language) {
      case 'fr':
        return (await import('antd/es/locale/fr_FR')).default;
      case 'es':
        return (await import('antd/es/locale/es_ES')).default;
      case 'pt':
        return (await import('antd/es/locale/pt_BR')).default;
      case 'it':
        return (await import('antd/es/locale/it_IT')).default;
      case 'de':
        return (await import('antd/es/locale/de_DE')).default;
      case 'en':
      default:
        return (await import('antd/es/locale/en_US')).default;
    }
  } catch (error) {
    console.error('Failed to load Ant Design locale, falling back to en_US:', error);
    return (await import('antd/es/locale/en_US')).default;
  }
};

export default i18n;
